import { buildPoyoPayload, formWithCharacterVoices, collectCharacterVoiceMap } from "../modelProfiles";

export { buildPoyoPayload };

const MEDIA_BUCKETS = [
  "imageUrlUploads",
  "referenceImageUploads",
  "referenceVideoUploads",
  "referenceAudioUploads",
];

/**
 * Collect auto visual identity locks from uploaded images (set after describe-image).
 * @param {object} form
 */
export function collectCharacterImageDescriptions(form) {
  const out = [];
  (form?.imageUrlUploads || []).forEach((item, index) => {
    const identity = String(item?.visualDescription || item?.identity || "").trim();
    if (!identity) return;
    out.push({
      label: String(item?.characterName || item?.name || `Character ${index + 1}`).trim(),
      bucket: "imageUrlUploads",
      index,
      identity,
    });
  });
  return out;
}

/**
 * Build multipart body: JSON payload + binary uploads (replaces blob preview URLs).
 * @param {object} form
 * @param {string} categoryId
 * @param {{ segmentOnly?: boolean; segmentIndex?: number; segmentTotal?: number }} [opts]
 */
export function buildVideoStudioFormData(form, categoryId, opts = {}) {
  const resolved = formWithCharacterVoices(form);
  const fd = new FormData();
  fd.append("categoryId", categoryId);
  fd.append("uiModelId", resolved.model || "");
  fd.append("title", String(resolved.title || "").trim());
  // Keep the unexpanded prompt (with @tags) for multi-speaker voice matching.
  const rawPrompt = String(resolved.prompt || "").trim();
  const characterVoices =
    resolved.characterVoices || collectCharacterVoiceMap(resolved);
  const imageDescriptions = collectCharacterImageDescriptions(resolved);
  const payload = buildPoyoPayload(resolved, categoryId);
  const libraryVoiceId = String(resolved.characterVoiceId || "").trim();
  payload._meta = {
    ...(payload._meta && typeof payload._meta === "object" ? payload._meta : {}),
    ...(rawPrompt ? { original_prompt_for_voice: rawPrompt } : {}),
    ...(characterVoices.length ? { character_voices: characterVoices } : {}),
    ...(imageDescriptions.length
      ? { character_image_descriptions: imageDescriptions }
      : {}),
    ...(libraryVoiceId ? { character_voice_id: libraryVoiceId } : {}),
  };
  fd.append("payload", JSON.stringify(payload));

  if (opts.segmentOnly) {
    fd.append("segmentOnly", "true");
    if (opts.segmentIndex != null) fd.append("segmentIndex", String(opts.segmentIndex));
    if (opts.segmentTotal != null) fd.append("segmentTotal", String(opts.segmentTotal));
  }

  for (const bucket of MEDIA_BUCKETS) {
    const items = resolved[bucket] || [];
    items.forEach((item, index) => {
      if (item?.file instanceof File) {
        fd.append(`${bucket}_${index}`, item.file);
      }
    });
  }

  return fd;
}

function readApiError(res, fallback) {
  if (res?.logout) {
    return "Session expired. Please sign in again.";
  }
  return res?.data?.msg || res?.error || fallback;
}

/**
 * @param {import('../../../context/GlobalContext').default} hitAxios
 * @param {object} form
 * @param {string} categoryId
 * @param {{ segmentOnly?: boolean; segmentIndex?: number; segmentTotal?: number }} [opts]
 * @returns {Promise<{ jobId: number, taskId: string, job: object }>}
 */
export async function generateVideo(hitAxios, form, categoryId, opts = {}) {
  const body = buildVideoStudioFormData(form, categoryId, opts);

  const res = await hitAxios({
    path: "/api/video-studio/jobs",
    post: true,
    obj: body,
    showLoading: false,
    showSnackbar: false,
    timeout: 120000,
  });

  if (res?.logout) {
    throw new Error("Session expired. Please sign in again.");
  }

  if (!res?.data?.success) {
    throw new Error(readApiError(res, "Could not start video generation."));
  }

  const job = res.data.data?.job;
  const taskId = res.data.data?.taskId || job?.task_id;
  if (!job?.id) {
    throw new Error("Server did not return a job id.");
  }

  return { jobId: job.id, taskId, job };
}

/**
 * @param {Function} hitAxios
 * @param {number} jobId
 */
export async function pollVideoJob(hitAxios, jobId) {
  const res = await hitAxios({
    path: `/api/video-studio/jobs/${jobId}/poll`,
    post: true,
    showLoading: false,
    showSnackbar: false,
  });

  if (res?.logout) {
    throw new Error("Session expired. Please sign in again.");
  }

  if (!res?.data?.success) {
    throw new Error(readApiError(res, "Could not poll video job."));
  }

  const job = res.data.data?.job;
  if (!job || typeof job.status !== "string") {
    throw new Error("Invalid job status response from server.");
  }

  return job;
}
